import React from "react";
import { motion } from "framer-motion";
import { BLOG_POSTS } from "../data/content";

export default function Blog() {
  const featured = BLOG_POSTS.find((p) => p.featured);
  const rest = BLOG_POSTS.filter((p) => !p.featured);

  return (
    <section id="insights" className="vw-section relative bg-[var(--vw-bg-soft)]" data-testid="vw-blog">
      <div className="vw-container">
        <div className="mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="vw-eyebrow mb-6">INSIGHTS · FROM THE FLOOR</div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.92] tracking-tight max-w-[18ch]">
              Field notes from producers who <span className="font-serif-italic font-normal text-[var(--vw-cyan)]">actually ship</span>.
            </h2>
          </div>
          <a href="#insights" className="vw-btn-ghost self-start md:self-end shrink-0" data-testid="vw-blog-all">
            All Insights
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </a>
        </div>

        {/* Featured Post */}
        {featured && (
          <motion.a
            href="#contact"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.7 }}
            className="group relative grid md:grid-cols-[1.1fr_1fr] gap-px bg-[var(--vw-border)] border border-[var(--vw-border-strong)] mb-8 overflow-hidden"
            data-testid={`vw-blog-featured-${featured.id}`}
          >
            <div className="relative aspect-[16/10] md:aspect-auto bg-black overflow-hidden">
              <img src={featured.thumb} alt={featured.title}
                   className="absolute inset-0 w-full h-full object-cover opacity-65 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-transparent to-transparent" />
              <div className="absolute top-4 left-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] border border-[var(--vw-cyan)] px-2.5 py-1 bg-black/50">
                <span className="w-1.5 h-1.5 bg-[var(--vw-cyan)] vw-blink" />
                FEATURED PLAYBOOK
              </div>
              <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
                {featured.tags.map((t) => (
                  <span key={t} className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/85 border border-white/30 px-2 py-0.5 bg-black/40">
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div className="relative bg-[var(--vw-bg)] p-8 md:p-12 flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]">
                <span className="text-[var(--vw-cyan)]">{featured.category}</span>
                <span className="w-4 h-px bg-[var(--vw-border-strong)]" />
                <span>{featured.readTime}</span>
                <span className="w-4 h-px bg-[var(--vw-border-strong)]" />
                <span>{featured.date}</span>
              </div>
              <h3 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[0.98] tracking-tight text-[var(--vw-text)] group-hover:text-[var(--vw-cyan)] transition-colors mb-6 max-w-[22ch]">
                {featured.title}
              </h3>
              <p className="text-[var(--vw-text-dim)] leading-relaxed text-lg max-w-lg mb-8">
                {featured.excerpt}
              </p>
              <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-[var(--vw-cyan)]">
                Read the Playbook
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="transition-transform group-hover:translate-x-1"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
              </div>
            </div>
          </motion.a>
        )}

        {/* Rest of posts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {rest.map((p, i) => (
            <motion.a
              href="#contact"
              key={p.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.1 }}
              className="group border border-[var(--vw-border)] hover:border-[var(--vw-cyan)] bg-[var(--vw-bg)] flex flex-col transition-colors"
              data-testid={`vw-blog-post-${p.id}`}
            >
              <div className="relative aspect-[16/10] bg-black overflow-hidden">
                <img src={p.thumb} alt={p.title}
                     className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                <div className="absolute top-3 left-3 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] border border-[var(--vw-cyan)]/60 px-2 py-0.5 bg-black/45">
                  {p.category}
                </div>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-3 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]">
                  <span>{p.readTime}</span><span>·</span><span>{p.date}</span>
                </div>
                <h4 className="font-display text-xl leading-[1.1] tracking-tight text-[var(--vw-text)] group-hover:text-[var(--vw-cyan)] transition-colors mb-3">
                  {p.title}
                </h4>
                <p className="text-sm text-[var(--vw-text-dim)] leading-relaxed line-clamp-3 flex-1">
                  {p.excerpt}
                </p>
                <div className="mt-5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-cyan)] opacity-70 group-hover:opacity-100 transition-opacity">
                  Read
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
